import { createApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { BasePageRequest, type BasePageResponse, type BaseRequest } from '../models'
import {
  BookingStatus,
  ReminderType,
  type AccountSchedulingSettings,
  type ArchivedBooking,
  type Booking,
  type EmailTemplate,
  type EventConfigInput,
  type EventTypeHost,
  type EventTypeHosts,
  type HostMode,
  type Location,
  type ReminderSettings,
  type ReminderSettingsPreview,
  type ReminderSettingsResponse,
  type ReminderSettingsUpdate,
  type SchedulingEventType,
  type SchedulingLink,
  type SchedulingSlot,
  type SchedulingUser,
  type SettingsResetScope,
  type SmsAreaCode,
  type SmsNumber,
  type SmsTemplate,
  type UnsubscribedClient,
  type UserSchedulingSettings,
} from '../../models/Scheduling'
import { type CancelBookingPayload, type CreateBookingPayload, type LocationInput, type RescheduleBookingPayload, type UpdateBookingPayload } from './dto'

const _BASE_URL = import.meta.env.REACT_APP_SCHEDULING_API_BASE_URL!

const availabilityUrl = (accountId: string) => `/accounts/${accountId}/availability`
const emailTemplatesUrl = (accountId: string) => `/accounts/${accountId}/email-templates`
const smsTemplatesUrl = (accountId: string) => `/accounts/${accountId}/sms-templates`
const smsNumbersUrl = (accountId: string) => `/accounts/${accountId}/sms/numbers`
const smsAreaCodesUrl = (accountId: string) => `/accounts/${accountId}/sms/area-codes`
const bookingsUrl = (accountId: string) => `/accounts/${accountId}/bookings`
const eventTypesUrl = (accountId: string) => `/accounts/${accountId}/event-types`
const usersUrl = (accountId: string) => `/accounts/${accountId}/users`
const userEventTypesUrl = (accountId: string, userId: string) => `/accounts/${accountId}/users/${userId}/event-types`
const schedulingLinksUrl = (accountId: string) => `/accounts/${accountId}/scheduling-links`
const locationsUrl = (accountId: string) => `/accounts/${accountId}/locations`
const userLocationsUrl = (accountId: string, userId: string) => `/accounts/${accountId}/users/${userId}/locations`
const accountSettingsUrl = (accountId: string) => `/accounts/${accountId}/scheduling-settings`
const userSettingsUrl = (accountId: string, userId: string) => `/accounts/${accountId}/users/${userId}/scheduling-settings`
const reminderSettingsUrl = (accountId: string) => `/accounts/${accountId}/reminder-settings`
const unsubscribesUrl = (accountId: string) => `/accounts/${accountId}/unsubscribes`

type PaginatedResult<T> = { data?: { data: T[] }; error?: FetchBaseQueryError }

export interface GetAvailabilityRequest extends BaseRequest {
  eventTypeId: string
  userId?: string
  dateFrom: string
  dateTo: string
}

interface BookingListFilters extends BasePageRequest {
  userIds?: string[]
  contactIds?: string[]
  eventTypeIds?: string[]
  search?: string
  dateFrom?: string
  dateTo?: string
}

export interface ListBookingsRequest extends BookingListFilters {
  status?: BookingStatus
}

export type ListArchivedBookingsRequest = BookingListFilters

const bookingListParams = (request: BookingListFilters) => ({
  ...(request.userIds?.length ? { userIds: request.userIds.join() } : {}),
  ...(request.contactIds?.length ? { contactIds: request.contactIds.join() } : {}),
  ...(request.eventTypeIds?.length ? { eventTypeIds: request.eventTypeIds.join() } : {}),
  ...(request.search ? { search: request.search } : {}),
  ...(request.dateFrom ? { dateFrom: request.dateFrom } : {}),
  ...(request.dateTo ? { dateTo: request.dateTo } : {}),
  ...request.pageRequest,
})

export interface GetBookingByIdRequest extends BaseRequest {
  bookingId: string
}

export interface GetSessionBookingsRequest extends BaseRequest {
  sessionId: string
  roomId: string
}

export interface BookingActionRequest extends BaseRequest {
  bookingId: string
}

export interface CreateBookingRequest extends BaseRequest {
  payload: CreateBookingPayload
}

export interface UpdateBookingRequest extends BaseRequest {
  payload: UpdateBookingPayload
}

export interface RescheduleBookingRequest extends BaseRequest {
  payload: RescheduleBookingPayload
}

export interface CancelBookingRequest extends BaseRequest {
  payload: CancelBookingPayload
}

export interface GetSchedulingEventTypesRequest extends BaseRequest {
  userId?: string
}

export interface GetEventTypeByIdRequest extends BaseRequest {
  eventTypeId: string
}

export interface UpsertEventTypeRequest extends BaseRequest {
  eventType: SchedulingEventType
  eventConfig?: EventConfigInput
}

export interface EventTypeActionRequest extends BaseRequest {
  eventTypeId: string
}

export interface ReplaceEventTypeHostsRequest extends EventTypeActionRequest {
  hostMode: HostMode
  userIds: string[]
}

export interface ListEventTypeHostCandidatesRequest extends EventTypeActionRequest {
  name?: string
  page: number
  size: number
}

export interface GetUserEventTypesRequest extends BaseRequest {
  userId: string
}

export interface GetSchedulingUsersRequest extends BaseRequest {
  eventTypeId: string
}

export interface SearchSchedulingLinksRequest extends BasePageRequest {
  eventTypeId?: string
  active?: boolean
}

export interface AddEventTypesToUserPageRequest extends BaseRequest {
  userId: string
  eventTypeIds: string[]
}

export interface RemoveEventTypeFromUserPageRequest extends BaseRequest {
  userId: string
  eventTypeId: string
}

export interface CreateLocationRequest extends BaseRequest {
  input: LocationInput
}

export interface UpdateLocationRequest extends BaseRequest {
  locationId: string
  input: LocationInput
}

export interface LocationActionRequest extends BaseRequest {
  locationId: string
}

export interface GetUserLocationsRequest extends BaseRequest {
  userId: string
}

export interface CreateUserLocationRequest extends BaseRequest {
  userId: string
  input: LocationInput
}

export interface UpdateUserLocationRequest extends BaseRequest {
  userId: string
  locationId: string
  input: LocationInput
}

export interface UserLocationActionRequest extends BaseRequest {
  userId: string
  locationId: string
}

export type AccountSchedulingSettingsUpdate = Partial<AccountSchedulingSettings>

export interface RequestSmsNumberRequest extends BaseRequest {
  areaCode: string
}

export interface CancelSmsNumberSetupRequest extends BaseRequest {
  numberId: string
}

export interface UpdateAccountSchedulingSettingsRequest extends BaseRequest {
  update: AccountSchedulingSettingsUpdate
}

export interface GetUserSchedulingSettingsRequest extends BaseRequest {
  userId: string
}

export type UserSchedulingSettingsUpdate = Partial<UserSchedulingSettings>

export interface UpdateUserSchedulingSettingsRequest extends BaseRequest {
  userId: string
  update: UserSchedulingSettingsUpdate
}

export interface ResetUserSchedulingSettingsRequest extends BaseRequest {
  userId: string
  scopes: SettingsResetScope[]
}

export interface UpdateReminderSettingsRequest extends BaseRequest {
  reminderType: ReminderType
  update: ReminderSettingsUpdate
}

export interface GetUnsubscribesRequest extends BaseRequest {
  emails: string[]
}

export const schedulingApi = createApi({
  reducerPath: 'schedulingApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: [
    'Booking',
    'SchedulingEventType',
    'SchedulingEventTypeHosts',
    'UserSchedulingEventTypes',
    'SchedulingLocation',
    'UserSchedulingLocation',
    'AccountSchedulingSettings',
    'UserSchedulingSettings',
    'SchedulingEmailTemplate',
    'SchedulingSmsTemplate',
    'SchedulingSmsNumber',
    'SchedulingUser',
    'SchedulingReminderSettings',
    'SchedulingUnsubscribe',
  ],
  endpoints: (builder) => ({
    getSchedulingEmailTemplates: builder.query<EmailTemplate[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<EmailTemplate>({ url: emailTemplatesUrl(request.accountId) }, baseQuery)) as PaginatedResult<EmailTemplate>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: ['SchedulingEmailTemplate'],
    }),
    getSchedulingSmsTemplates: builder.query<SmsTemplate[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<SmsTemplate>({ url: smsTemplatesUrl(request.accountId) }, baseQuery)) as PaginatedResult<SmsTemplate>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: ['SchedulingSmsTemplate'],
    }),
    getSmsNumbers: builder.query<SmsNumber[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<SmsNumber>({ url: smsNumbersUrl(request.accountId) }, baseQuery)) as PaginatedResult<SmsNumber>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: ['SchedulingSmsNumber'],
    }),
    requestSmsNumber: builder.mutation<SmsNumber, RequestSmsNumberRequest>({
      query: (request) => ({ url: smsNumbersUrl(request.accountId), method: 'POST', body: { areaCode: request.areaCode } }),
      invalidatesTags: ['SchedulingSmsNumber'],
    }),
    cancelSmsNumberSetup: builder.mutation<void, CancelSmsNumberSetupRequest>({
      query: (request) => ({ url: `${smsNumbersUrl(request.accountId)}/${request.numberId}`, method: 'DELETE' }),
      invalidatesTags: ['SchedulingSmsNumber'],
    }),
    getSmsAreaCodes: builder.query<SmsAreaCode[], BaseRequest>({
      query: (request) => ({ url: smsAreaCodesUrl(request.accountId) }),
      transformResponse: (response: { data: SmsAreaCode[] }) => response.data,
      keepUnusedDataFor: Infinity,
    }),
    getSchedulingAvailability: builder.query<SchedulingSlot[], GetAvailabilityRequest>({
      query: (request) => ({
        url: availabilityUrl(request.accountId),
        params: {
          eventTypeId: request.eventTypeId,
          dateFrom: request.dateFrom,
          dateTo: request.dateTo,
          ...(request.userId ? { userId: request.userId } : {}),
        },
      }),
      transformResponse: (response: { slots?: SchedulingSlot[] }) => response.slots ?? [],
    }),
    getBookings: builder.query<BasePageResponse<Booking[]>, ListBookingsRequest>({
      query: (request) => ({
        url: bookingsUrl(request.accountId),
        params: {
          ...(request.status ? { status: request.status } : {}),
          ...bookingListParams(request),
        },
      }),
      providesTags: (result) => [
        { type: 'Booking', id: 'LIST' },
        ...(result?.data ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId })),
      ],
    }),
    getArchivedBookings: builder.query<BasePageResponse<ArchivedBooking[]>, ListArchivedBookingsRequest>({
      query: (request) => ({
        url: `${bookingsUrl(request.accountId)}/archive`,
        params: bookingListParams(request),
      }),
      providesTags: (result) => [
        { type: 'Booking', id: 'LIST' },
        ...(result?.data ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId })),
      ],
    }),
    getAllBookings: builder.query<Booking[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Booking>({ url: bookingsUrl(request.accountId) }, baseQuery)) as PaginatedResult<Booking>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [{ type: 'Booking', id: 'LIST' }, ...(result ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId }))],
    }),
    getSessionBookings: builder.query<Booking[], GetSessionBookingsRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Booking>(
          { url: bookingsUrl(request.accountId), params: { sourceType: 'session', sourceIds: `sessionId:${request.sessionId},roomId:${request.roomId}` } },
          baseQuery,
        )) as PaginatedResult<Booking>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [{ type: 'Booking', id: 'LIST' }, ...(result ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId }))],
    }),
    getBookingById: builder.query<Booking, GetBookingByIdRequest>({
      query: (request) => `${bookingsUrl(request.accountId)}/${request.bookingId}`,
      providesTags: (_result, _error, request) => [{ type: 'Booking', id: request.bookingId }],
    }),
    acceptBooking: builder.mutation<Booking, BookingActionRequest>({
      query: (request) => ({ url: `${bookingsUrl(request.accountId)}/${request.bookingId}/accept`, method: 'POST' }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.bookingId },
      ],
    }),
    createBooking: builder.mutation<Booking, CreateBookingRequest>({
      query: (request) => ({ url: bookingsUrl(request.accountId), method: 'POST', body: request.payload }),
      invalidatesTags: [{ type: 'Booking', id: 'LIST' }],
    }),
    updateBooking: builder.mutation<Booking, UpdateBookingRequest>({
      query: (request) => {
        const { bookingId, ...body } = request.payload
        return { url: `${bookingsUrl(request.accountId)}/${bookingId}`, method: 'PATCH', body }
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.payload.bookingId },
      ],
    }),
    rescheduleBooking: builder.mutation<Booking, RescheduleBookingRequest>({
      query: (request) => {
        const { bookingId, ...body } = request.payload
        return { url: `${bookingsUrl(request.accountId)}/${bookingId}/reschedule`, method: 'POST', body }
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.payload.bookingId },
      ],
    }),
    cancelBooking: builder.mutation<Booking, CancelBookingRequest>({
      query: (request) => {
        const { bookingId, ...body } = request.payload
        return { url: `${bookingsUrl(request.accountId)}/${bookingId}/cancel`, method: 'POST', body }
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.payload.bookingId },
      ],
    }),
    archiveBooking: builder.mutation<Booking, BookingActionRequest>({
      query: (request) => ({ url: `${bookingsUrl(request.accountId)}/${request.bookingId}/archive`, method: 'POST' }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.bookingId },
      ],
    }),
    dismissBooking: builder.mutation<Booking, BookingActionRequest>({
      query: (request) => ({ url: `${bookingsUrl(request.accountId)}/${request.bookingId}/dismiss`, method: 'POST' }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.bookingId },
      ],
    }),
    sendReminder: builder.mutation<Booking, BookingActionRequest>({
      query: (request) => ({ url: `${bookingsUrl(request.accountId)}/${request.bookingId}/reminders/follow_up/send`, method: 'POST' }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.bookingId },
      ],
    }),
    getSchedulingEventTypes: builder.query<SchedulingEventType[], GetSchedulingEventTypesRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = eventTypesUrl(request.accountId)
        const scope = request.userId ? { userId: request.userId } : {}
        const active = (await paginate<SchedulingEventType>({ url, params: { ...scope, active: true } }, baseQuery)) as PaginatedResult<SchedulingEventType>
        if (active.error) {
          return { error: active.error }
        }
        const inactive = (await paginate<SchedulingEventType>({ url, params: { ...scope, active: false } }, baseQuery)) as PaginatedResult<SchedulingEventType>
        if (inactive.error) {
          return { error: inactive.error }
        }
        return { data: [...(active.data?.data ?? []), ...(inactive.data?.data ?? [])] }
      },
      providesTags: (result) => [
        { type: 'SchedulingEventType', id: 'LIST' },
        ...(result ?? []).map((eventType) => ({ type: 'SchedulingEventType' as const, id: eventType.id })),
      ],
    }),
    getSchedulingUsers: builder.query<SchedulingUser[], GetSchedulingUsersRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<SchedulingUser>(
          { url: usersUrl(request.accountId), params: { eventTypeId: request.eventTypeId } },
          baseQuery,
        )) as PaginatedResult<SchedulingUser>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: ['SchedulingUser'],
    }),
    searchSchedulingLinks: builder.query<BasePageResponse<SchedulingLink[]>, SearchSchedulingLinksRequest>({
      query: (request) => ({
        url: schedulingLinksUrl(request.accountId),
        params: {
          ...(request.eventTypeId ? { eventTypeId: request.eventTypeId } : {}),
          ...(request.active != null ? { active: request.active } : {}),
          ...request.pageRequest,
        },
      }),
    }),
    getSchedulingEventTypeById: builder.query<SchedulingEventType, GetEventTypeByIdRequest>({
      query: (request) => `${eventTypesUrl(request.accountId)}/${request.eventTypeId}`,
      providesTags: (_result, _error, request) => [{ type: 'SchedulingEventType', id: request.eventTypeId }],
    }),
    upsertSchedulingEventType: builder.mutation<SchedulingEventType, UpsertEventTypeRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = eventTypesUrl(request.accountId)
        const body = { ...request.eventType, eventConfig: request.eventConfig }
        delete body.hostSummary
        let eventTypeId = request.eventType.id
        if (!eventTypeId) {
          const created = await baseQuery({ url, method: 'POST', body })
          if (created.error) {
            return { error: created.error }
          }
          eventTypeId = (created.data as SchedulingEventType).id
        }
        const updated = await baseQuery({ url: `${url}/${eventTypeId}`, method: 'PATCH', body: { ...body, id: eventTypeId } })
        if (updated.error) {
          return { error: updated.error }
        }
        return { data: updated.data as SchedulingEventType }
      },
      invalidatesTags: (result) => [{ type: 'SchedulingEventType', id: 'LIST' }, ...(result ? [{ type: 'SchedulingEventType' as const, id: result.id }] : [])],
    }),
    getUserSchedulingEventTypes: builder.query<string[], GetUserEventTypesRequest>({
      query: (request) => userEventTypesUrl(request.accountId, request.userId),
      transformResponse: (response: { eventTypeIds?: string[] }) => response.eventTypeIds ?? [],
      providesTags: ['UserSchedulingEventTypes'],
    }),
    addSchedulingEventTypesToUserPage: builder.mutation<string[], AddEventTypesToUserPageRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = userEventTypesUrl(request.accountId, request.userId)
        const current = await baseQuery(url)
        if (current.error) {
          return { error: current.error }
        }
        const currentIds = (current.data as { eventTypeIds?: string[] }).eventTypeIds ?? []
        const eventTypeIds = Array.from(new Set([...currentIds, ...request.eventTypeIds]))
        const result = await baseQuery({ url, method: 'PUT', body: { eventTypeIds } })
        if (result.error) {
          return { error: result.error }
        }
        return { data: (result.data as { eventTypeIds?: string[] }).eventTypeIds ?? eventTypeIds }
      },
      invalidatesTags: ['UserSchedulingEventTypes', { type: 'SchedulingEventType', id: 'LIST' }],
    }),
    removeSchedulingEventTypeFromUserPage: builder.mutation<string[], RemoveEventTypeFromUserPageRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = userEventTypesUrl(request.accountId, request.userId)
        const current = await baseQuery(url)
        if (current.error) {
          return { error: current.error }
        }
        const currentIds = (current.data as { eventTypeIds?: string[] }).eventTypeIds ?? []
        const eventTypeIds = currentIds.filter((id) => id !== request.eventTypeId)
        const result = await baseQuery({ url, method: 'PUT', body: { eventTypeIds } })
        if (result.error) {
          return { error: result.error }
        }
        return { data: (result.data as { eventTypeIds?: string[] }).eventTypeIds ?? eventTypeIds }
      },
      invalidatesTags: ['UserSchedulingEventTypes', { type: 'SchedulingEventType', id: 'LIST' }],
    }),
    getEventTypeHosts: builder.query<EventTypeHosts, EventTypeActionRequest>({
      query: (request) => `${eventTypesUrl(request.accountId)}/${request.eventTypeId}/hosts`,
      providesTags: (_result, _error, request) => [{ type: 'SchedulingEventTypeHosts', id: request.eventTypeId }],
    }),
    replaceEventTypeHosts: builder.mutation<EventTypeHosts, ReplaceEventTypeHostsRequest>({
      query: (request) => ({
        url: `${eventTypesUrl(request.accountId)}/${request.eventTypeId}/hosts`,
        method: 'PUT',
        body: { hostMode: request.hostMode, userIds: request.userIds },
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'SchedulingEventTypeHosts', id: request.eventTypeId },
        { type: 'SchedulingEventType', id: request.eventTypeId },
      ],
    }),
    getEventTypeHostCandidates: builder.query<BasePageResponse<EventTypeHost[]>, ListEventTypeHostCandidatesRequest>({
      query: (request) => ({
        url: `${eventTypesUrl(request.accountId)}/${request.eventTypeId}/hosts/candidates`,
        params: { page: request.page, size: request.size, ...(request.name ? { name: request.name } : {}) },
      }),
      providesTags: (_result, _error, request) => [{ type: 'SchedulingEventTypeHosts', id: request.eventTypeId }],
    }),
    duplicateSchedulingEventType: builder.mutation<SchedulingEventType, EventTypeActionRequest>({
      query: (request) => ({ url: `${eventTypesUrl(request.accountId)}/${request.eventTypeId}/duplicate`, method: 'POST' }),
      invalidatesTags: [{ type: 'SchedulingEventType', id: 'LIST' }],
    }),
    deleteSchedulingEventType: builder.mutation<{ id: string }, EventTypeActionRequest>({
      query: (request) => ({ url: `${eventTypesUrl(request.accountId)}/${request.eventTypeId}`, method: 'DELETE' }),
      transformResponse: (_response: unknown, _meta, request) => ({ id: request.eventTypeId }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'SchedulingEventType', id: 'LIST' },
        { type: 'SchedulingEventType', id: request.eventTypeId },
      ],
    }),
    getSchedulingLocations: builder.query<Location[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Location>({ url: locationsUrl(request.accountId) }, baseQuery)) as PaginatedResult<Location>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [
        { type: 'SchedulingLocation', id: 'LIST' },
        ...(result ?? []).map((location) => ({ type: 'SchedulingLocation' as const, id: location.id })),
      ],
    }),
    createSchedulingLocation: builder.mutation<Location, CreateLocationRequest>({
      query: (request) => ({ url: locationsUrl(request.accountId), method: 'POST', body: request.input }),
      invalidatesTags: [{ type: 'SchedulingLocation', id: 'LIST' }],
    }),
    updateSchedulingLocation: builder.mutation<Location, UpdateLocationRequest>({
      query: (request) => ({ url: `${locationsUrl(request.accountId)}/${request.locationId}`, method: 'PATCH', body: request.input }),
      invalidatesTags: (result) => [{ type: 'SchedulingLocation', id: 'LIST' }, ...(result ? [{ type: 'SchedulingLocation' as const, id: result.id }] : [])],
    }),
    deleteSchedulingLocation: builder.mutation<{ id: string }, LocationActionRequest>({
      query: (request) => ({ url: `${locationsUrl(request.accountId)}/${request.locationId}`, method: 'DELETE' }),
      transformResponse: (_response: unknown, _meta, request) => ({ id: request.locationId }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'SchedulingLocation', id: 'LIST' },
        { type: 'SchedulingLocation', id: request.locationId },
      ],
    }),
    getUserSchedulingLocations: builder.query<Location[], GetUserLocationsRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Location>({ url: userLocationsUrl(request.accountId, request.userId) }, baseQuery)) as PaginatedResult<Location>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [
        { type: 'UserSchedulingLocation', id: 'LIST' },
        ...(result ?? []).map((location) => ({ type: 'UserSchedulingLocation' as const, id: location.id })),
      ],
    }),
    createUserSchedulingLocation: builder.mutation<Location, CreateUserLocationRequest>({
      query: (request) => ({ url: userLocationsUrl(request.accountId, request.userId), method: 'POST', body: request.input }),
      invalidatesTags: [{ type: 'UserSchedulingLocation', id: 'LIST' }],
    }),
    updateUserSchedulingLocation: builder.mutation<Location, UpdateUserLocationRequest>({
      query: (request) => ({ url: `${userLocationsUrl(request.accountId, request.userId)}/${request.locationId}`, method: 'PATCH', body: request.input }),
      invalidatesTags: (result) => [
        { type: 'UserSchedulingLocation', id: 'LIST' },
        ...(result ? [{ type: 'UserSchedulingLocation' as const, id: result.id }] : []),
      ],
    }),
    deleteUserSchedulingLocation: builder.mutation<{ id: string }, UserLocationActionRequest>({
      query: (request) => ({ url: `${userLocationsUrl(request.accountId, request.userId)}/${request.locationId}`, method: 'DELETE' }),
      transformResponse: (_response: unknown, _meta, request) => ({ id: request.locationId }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'UserSchedulingLocation', id: 'LIST' },
        { type: 'UserSchedulingLocation', id: request.locationId },
      ],
    }),
    getAccountSchedulingSettings: builder.query<AccountSchedulingSettings, BaseRequest>({
      query: (request) => accountSettingsUrl(request.accountId),
      providesTags: ['AccountSchedulingSettings'],
    }),
    updateAccountSchedulingSettings: builder.mutation<AccountSchedulingSettings, UpdateAccountSchedulingSettingsRequest>({
      query: (request) => ({ url: accountSettingsUrl(request.accountId), method: 'PATCH', body: request.update }),
      invalidatesTags: ['AccountSchedulingSettings'],
    }),
    getUserSchedulingSettings: builder.query<UserSchedulingSettings, GetUserSchedulingSettingsRequest>({
      query: (request) => userSettingsUrl(request.accountId, request.userId),
      providesTags: ['UserSchedulingSettings'],
    }),
    updateUserSchedulingSettings: builder.mutation<UserSchedulingSettings, UpdateUserSchedulingSettingsRequest>({
      query: (request) => ({ url: userSettingsUrl(request.accountId, request.userId), method: 'PATCH', body: request.update }),
      invalidatesTags: ['UserSchedulingSettings'],
    }),
    resetUserSchedulingSettings: builder.mutation<UserSchedulingSettings, ResetUserSchedulingSettingsRequest>({
      query: (request) => ({
        url: `${userSettingsUrl(request.accountId, request.userId)}/reset`,
        method: 'POST',
        body: { scopes: request.scopes },
      }),
      invalidatesTags: ['UserSchedulingSettings'],
    }),
    getReminderSettings: builder.query<ReminderSettingsResponse, BaseRequest>({
      query: (request) => reminderSettingsUrl(request.accountId),
      providesTags: ['SchedulingReminderSettings'],
    }),
    putReminderSettings: builder.mutation<ReminderSettings, UpdateReminderSettingsRequest>({
      query: (request) => ({ url: `${reminderSettingsUrl(request.accountId)}/${request.reminderType}`, method: 'PUT', body: request.update }),
      invalidatesTags: ['SchedulingReminderSettings'],
    }),
    previewReminderSettings: builder.mutation<ReminderSettingsPreview, UpdateReminderSettingsRequest>({
      query: (request) => ({ url: `${reminderSettingsUrl(request.accountId)}/${request.reminderType}/preview`, method: 'POST', body: request.update }),
    }),
    getUnsubscribes: builder.query<UnsubscribedClient[], GetUnsubscribesRequest>({
      query: (request) => ({ url: unsubscribesUrl(request.accountId), params: { email: request.emails.join() } }),
      providesTags: ['SchedulingUnsubscribe'],
    }),
  }),
})

export const {
  useGetSchedulingEmailTemplatesQuery,
  useGetSchedulingSmsTemplatesQuery,
  useGetSmsNumbersQuery,
  useRequestSmsNumberMutation,
  useCancelSmsNumberSetupMutation,
  useGetSmsAreaCodesQuery,
  useGetSchedulingAvailabilityQuery,
  useGetBookingsQuery,
  useGetArchivedBookingsQuery,
  useGetSessionBookingsQuery,
  useGetAllBookingsQuery,
  useGetBookingByIdQuery,
  useAcceptBookingMutation,
  useCreateBookingMutation,
  useRescheduleBookingMutation,
  useUpdateBookingMutation,
  useCancelBookingMutation,
  useArchiveBookingMutation,
  useDismissBookingMutation,
  useSendReminderMutation,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingUsersQuery,
  useSearchSchedulingLinksQuery,
  useGetSchedulingEventTypeByIdQuery,
  useGetEventTypeHostsQuery,
  useReplaceEventTypeHostsMutation,
  useGetEventTypeHostCandidatesQuery,
  useLazyGetEventTypeHostCandidatesQuery,
  useUpsertSchedulingEventTypeMutation,
  useGetUserSchedulingEventTypesQuery,
  useAddSchedulingEventTypesToUserPageMutation,
  useRemoveSchedulingEventTypeFromUserPageMutation,
  useDuplicateSchedulingEventTypeMutation,
  useDeleteSchedulingEventTypeMutation,
  useGetSchedulingLocationsQuery,
  useCreateSchedulingLocationMutation,
  useUpdateSchedulingLocationMutation,
  useDeleteSchedulingLocationMutation,
  useGetUserSchedulingLocationsQuery,
  useCreateUserSchedulingLocationMutation,
  useUpdateUserSchedulingLocationMutation,
  useDeleteUserSchedulingLocationMutation,
  useGetAccountSchedulingSettingsQuery,
  useUpdateAccountSchedulingSettingsMutation,
  useGetUserSchedulingSettingsQuery,
  useUpdateUserSchedulingSettingsMutation,
  useResetUserSchedulingSettingsMutation,
  useGetReminderSettingsQuery,
  usePutReminderSettingsMutation,
  usePreviewReminderSettingsMutation,
  useGetUnsubscribesQuery,
} = schedulingApi
